import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// REACT_APP_BACKEND_URL = base URL without /api (e.g. https://host:8001)
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  // Track if the user was set programmatically (login/register) to skip fetchCurrentUser
  const skipFetch = useRef(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (skipFetch.current) {
        // Already set user data from login/register – no need to re-fetch
        skipFetch.current = false;
        setLoading(false);
      } else {
        fetchCurrentUser();
      }
    } else {
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const storedCompanyId = localStorage.getItem('company_id') || '';
      const storedUserId = localStorage.getItem('user_id') || '';
      if (storedCompanyId) {
        axios.defaults.headers.common['X-Company-ID'] = storedCompanyId;
      }
      if (storedUserId) {
        axios.defaults.headers.common['X-User-ID'] = storedUserId;
      }
      const response = await axios.get(`${API_URL}/api/auth/me`);
      const userData = response.data;
      setUser(userData);
      // Refresh stored IDs from server response
      if (userData.company_id) {
        localStorage.setItem('company_id', userData.company_id);
        axios.defaults.headers.common['X-Company-ID'] = userData.company_id;
      }
      if (userData.id) {
        localStorage.setItem('user_id', userData.id);
        axios.defaults.headers.common['X-User-ID'] = userData.id;
      }
    } catch (error) {
      // Session invalid or expired — silently log out (don't expose error details)
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('[Auth] Failed to validate session – logging out');
      }
      logout();
    } finally {
      setLoading(false);
    }
  };

  const _applySession = (data) => {
    const { access_token, user: userData } = data;
    skipFetch.current = true;
    localStorage.setItem('token', access_token);
    localStorage.setItem('company_id', userData.company_id || '');
    localStorage.setItem('user_id', userData.id || '');
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    axios.defaults.headers.common['X-Company-ID'] = userData.company_id || '';
    axios.defaults.headers.common['X-User-ID'] = userData.id || '';
    setUser(userData);
    setToken(access_token);
    return userData;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    // MFA gate: server returns { requires_mfa, mfa_token } when TOTP is on.
    // Caller must follow up with verifyMFA(mfa_token, code).
    if (response.data?.requires_mfa) {
      return { requires_mfa: true, mfa_token: response.data.mfa_token };
    }
    return _applySession(response.data);
  };

  const verifyMFA = async (mfaToken, code) => {
    const response = await axios.post(`${API_URL}/api/auth/verify-mfa`, {
      mfa_token: mfaToken,
      code,
    });
    return _applySession(response.data);
  };

  const register = async (email, password, full_name, company_name, company_name_en, currency, industry) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      email,
      password,
      full_name,
      company_name,
      ...(company_name_en && { company_name_en }),
      ...(currency && { currency }),
      ...(industry && { industry }),
    });
    const { access_token, user: userData } = response.data;
    skipFetch.current = true;
    localStorage.setItem('token', access_token);
    localStorage.setItem('company_id', userData.company_id || '');
    localStorage.setItem('user_id', userData.id || '');
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    axios.defaults.headers.common['X-Company-ID'] = userData.company_id || '';
    axios.defaults.headers.common['X-User-ID'] = userData.id || '';
    setUser(userData);
    setToken(access_token);
    return userData;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('company_id');
    localStorage.removeItem('user_id');
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Company-ID'];
    delete axios.defaults.headers.common['X-User-ID'];
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyMFA, register, logout, API_URL }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
